import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Maximize2 } from "lucide-react";

import PageHero from "@/components/PageHero";
import Seo from "@/components/Seo";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PROJECTS } from "@/lib/site";

const HERO_IMG =
  "/images/9ae3kktf.png";

const CATEGORIES = ["All", "Resinous", "Residential", "Urethane", "Stucco"];

export default function Projects() {
  const [filter, setFilter] = useState("All");

  const filtered = useMemo(() => {
    if (filter === "All") return PROJECTS;
    return PROJECTS.filter((p) => p.category === filter);
  }, [filter]);

  return (
    <div data-testid="page-projects">
      <Seo
        title="Projects"
        description="Selected industrial flooring, residential epoxy, food-grade urethane and stucco projects delivered across the Greater Toronto Area."
        path="/projects"
      />
      <PageHero
        eyebrow="Selected Work"
        title="Surfaces that have earned their wear."
        subtitle="A look at recent installs across food &amp; beverage, manufacturing, logistics and architectural exteriors."
        image={HERO_IMG}
      />

      <section className="section-pad">
        <div className="container-max">
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList
              data-testid="projects-tabs"
              className="bg-transparent gap-2 flex-wrap h-auto p-0 border-b border-zinc-800 w-full justify-start rounded-none"
            >
              {CATEGORIES.map((c) => (
                <TabsTrigger
                  key={c}
                  value={c}
                  data-testid={`tab-${c.toLowerCase()}`}
                  className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-[#FF5A00] data-[state=active]:border-b-2 data-[state=active]:border-[#FF5A00] text-zinc-400 hover:text-zinc-200 font-heading uppercase tracking-[0.2em] text-xs px-5 py-4 border-b-2 border-transparent"
                >
                  {c}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {filtered.map((p) => (
              <article
                key={p.id}
                data-testid={`project-${p.id}`}
                className="group relative overflow-hidden bg-zinc-900 border border-zinc-800 hover:border-[#FF5A00] transition-colors"
              >
                <div
                  className="aspect-[4/3] bg-cover bg-center scale-100 group-hover:scale-105 transition-transform duration-700"
                  style={{ backgroundImage: `url(${p.image})` }}
                />
                <div className="p-6">
                  <div className="font-heading text-[10px] tracking-[0.3em] uppercase text-[#FF5A00]">
                    {p.category}
                  </div>
                  <h3 className="font-display text-2xl tracking-wider uppercase text-zinc-50 mt-2 leading-tight">
                    {p.title}
                  </h3>
                  <div className="flex items-center gap-5 mt-4 text-xs text-zinc-400 font-heading tracking-wider uppercase">
                    <span className="inline-flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-[#FF5A00]" />
                      {p.location}
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <Maximize2 className="w-3.5 h-3.5 text-[#FF5A00]" />
                      {p.area}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {filtered.length === 0 && (
            <p className="text-zinc-500 text-center py-20 font-heading tracking-widest uppercase text-sm">
              No projects in this category yet.
            </p>
          )}
        </div>
      </section>

      <section className="section-pad bg-zinc-900 border-t border-zinc-800">
        <div className="container-max text-center">
          <h2 className="font-display text-5xl lg:text-7xl uppercase tracking-wider text-zinc-50">
            Your facility next?
          </h2>
          <p className="text-zinc-400 mt-6 max-w-2xl mx-auto text-lg">
            Walk us through the project. We&apos;ll come back with a system, a
            timeline and a number you can underwrite.
          </p>
          <Link to="/contact" data-testid="projects-final-cta" className="btn-primary mt-10">
            Start a Project <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
